import { supabase } from '@/lib/supabase';
import { isValidUUID, sanitizeText, isInRange, isNonEmptyString } from '@/utils/validation';
import { localStorageCache, CACHE_DURATIONS } from '@/utils/cacheUtils';
import { logError as logger, logInfo, logWarn, logDebug } from '@/utils/logger';
import { 
  selectServicePartition, 
  canMakeServiceRequest, 
  trackServiceResult,
  generateShardKey,
  SHARD_TYPES
} from '@/utils/scalingStrategies';

// Simple in-memory API rate limiter
class ApiRateLimiter {
  private limits: Map<string, { count: number, resetAt: number }>;
  private windowMs: number;
  private maxRequests: number;

  constructor(windowMs = 60 * 1000, maxRequests = 20) {
    this.limits = new Map();
    this.windowMs = windowMs;
    this.maxRequests = maxRequests;
    
    setInterval(() => this.cleanup(), 60 * 1000);
  }

  checkLimit(userId: string, operation: string): boolean {
    const key = `${userId}:${operation}`;
    const now = Date.now();
    const entry = this.limits.get(key);
    
    if (!entry || entry.resetAt < now) {
      this.limits.set(key, {
        count: 1,
        resetAt: now + this.windowMs
      });
      return true;
    }
    
    if (entry.count >= this.maxRequests) {
      return false;
    }
    
    entry.count += 1;
    this.limits.set(key, entry);
    
    return true;
  }
  
  private cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.limits.entries()) {
      if (entry.resetAt < now) {
        this.limits.delete(key);
      }
    }
  }
}

const apiRateLimiter = new ApiRateLimiter();

// Error handling utility for Supabase operations
export const handleSupabaseError = (error: any, defaultMessage: string = 'Database operation failed') => {
  if (error) {
    logger('Supabase error', 'db-utils', error);
    return {
      status: error.code || 500,
      error: error.message || defaultMessage,
      data: null
    };
  }
  return null;
};

// Validate detection type
export const validateDetectionType = (type: string) => {
  if (!type || typeof type !== 'string') {
    return false;
  }
  
  const sanitizedType = sanitizeText(type);
  const validTypes = ['text_analysis', 'image_analysis', 'video_analysis', 'voice_analysis', 'document_analysis'];
  return validTypes.includes(sanitizedType);
};

// Helper function to query detection results with sharding support
export const toDetectionResultsQuery = (userId?: string) => {
  const query = supabase.from('detection_results');
  
  if (userId) {
    const shardKey = generateShardKey(SHARD_TYPES.USER, userId);
    logDebug(`Using shard key for query: ${shardKey}`, 'sharding');
  }
  
  return query;
};

// Check if user has admin role with caching
export const isUserAdmin = async (userId: string) => {
  if (!isValidUUID(userId)) {
    console.error('Invalid user ID format');
    return false;
  }
  
  const cacheKey = `admin:${userId}`;
  const cachedValue = localStorageCache.get<boolean>(cacheKey);
  if (cachedValue !== null) {
    return cachedValue;
  }
  
  const { data, error } = await supabase
    .from('profiles')
    .select('role')
    .eq('id', userId)
    .single();
  
  const isAdmin = !error && data && data.role === 'admin';
  
  localStorageCache.set(cacheKey, isAdmin, CACHE_DURATIONS.MEDIUM);
  
  return isAdmin;
};

// Normalize pagination parameters
export const normalizePaginationParams = (limit: number, offset: number) => {
  const safeLimit = typeof limit === 'number' ? limit : 10;
  const safeOffset = typeof offset === 'number' ? offset : 0;
  
  return {
    normalizedLimit: Math.min(Math.max(1, safeLimit), 100),
    normalizedOffset: Math.max(0, safeOffset)
  };
};

// Sanitize input to prevent SQL injection
export const sanitizeInput = (input: string) => {
  if (!input || typeof input !== 'string') {
    return '';
  }
  return sanitizeText(input);
};

// Get detection statistics for a user with caching, circuit breaking, and service partitioning
export const getUserDetectionStats = async (userId: string) => {
  try {
    if (!isValidUUID(userId)) {
      return {
        status: 400,
        error: "Invalid user ID format",
        data: null
      };
    }
    
    if (!canMakeServiceRequest(userId, 'user_stats', 'getUserDetectionStats')) {
      return {
        status: 429,
        error: "Service temporarily unavailable or rate limit exceeded. Please try again later.",
        data: null
      };
    }
    
    const cacheKey = `stats:${userId}`;
    const cachedStats = localStorageCache.get(cacheKey);
    if (cachedStats) {
      return {
        status: 200,
        data: cachedStats,
        count: null
      };
    }
    
    const partition = selectServicePartition('user_stats', userId);
    if (partition) {
      logInfo(`Selected partition for user stats: ${partition.id} in ${partition.region}`, 'scaling');
    }

    const { data, error, count } = await toDetectionResultsQuery(userId)
      .select('*', { count: 'exact' })
      .eq('user_id', userId);

    if (error) {
      trackServiceResult('user_stats', false);
      return handleSupabaseError(error);
    }
    
    trackServiceResult('user_stats', true);
    
    if (data) {
      localStorageCache.set(cacheKey, data, 15 * 60 * 1000);
    }

    return {
      status: 200,
      data,
      count
    };
  } catch (error) {
    trackServiceResult('user_stats', false);
    return handleSupabaseError(error);
  }
};

// Enhanced error logging with structured data
export const logError = (context: string, error: any, metadata: Record<string, any> = {}) => {
  logger(`Error in ${context}`, context, {
    message: error.message || 'Unknown error',
    code: error.code,
    details: error.details,
    ...metadata
  });
};

// Rate limiting helper with caching
export const checkRateLimit = async (userId: string, action: string, maxRequests: number = 100, windowSeconds: number = 3600) => {
  if (!isValidUUID(userId)) {
    logError('Rate limiting check', new Error('Invalid user ID format'), { userId, action });
    return true;
  }
  
  if (!isNonEmptyString(action)) {
    logError('Rate limiting check', new Error('Invalid action parameter'), { userId, action });
    return true;
  }
  
  const cacheKey = `rateLimit:${userId}:${action}`;
  const cachedStatus = localStorageCache.get<boolean>(cacheKey);
  
  if (cachedStatus === false) {
    return false;
  }
  
  const safeMaxRequests = isInRange(maxRequests, 1, 1000) ? maxRequests : 100;
  const safeWindowSeconds = isInRange(windowSeconds, 1, 86400) ? windowSeconds : 3600;
  
  const windowStart = new Date();
  windowStart.setSeconds(windowStart.getSeconds() - safeWindowSeconds);
  
  const { count, error } = await supabase
    .from('detection_results')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .gte('created_at', windowStart.toISOString());
    
  if (error) {
    logError('Rate limiting check', error, { userId, action });
    return true;
  }
  
  const isLimited = (count || 0) >= safeMaxRequests;
  
  if (isLimited) {
    localStorageCache.set(cacheKey, false, 60 * 1000);
  }
  
  return !isLimited;
};

// Data validation helper
export const validateDetectionData = (data: any) => {
  if (!data || typeof data !== 'object') {
    return {
      valid: false,
      errors: 'Invalid data object'
    };
  }
  
  const requiredFields = ['user_id', 'analysis_type'];
  const missingFields = requiredFields.filter(field => !data[field]);
  
  if (missingFields.length > 0) {
    return {
      valid: false,
      errors: `Missing required fields: ${missingFields.join(', ')}`
    };
  }
  
  if (!isValidUUID(data.user_id)) {
    return {
      valid: false,
      errors: 'Invalid user ID format'
    };
  }
  
  if (typeof data.score === 'number' && !isInRange(data.score, 0, 1)) {
    return {
      valid: false,
      errors: 'Score must be between 0 and 1'
    };
  }
  
  return { valid: true, errors: null };
};

export { apiRateLimiter };
